import { LitElement, PropertyValues, css, unsafeCSS, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { ref, createRef, Ref } from "lit/directives/ref.js";
import { classMap } from "lit/directives/class-map.js";
import { repeat } from "lit/directives/repeat.js";
import { MenuItemState } from ".";

import "@shoelace-style/shoelace/dist/themes/light.css";
import "@shoelace-style/shoelace/dist/themes/dark.css";

import shoelaceStekzTheme from "@/wc/text-editor/styles/shoelace-stekz-theme.scss?inline";

import { registerIconLibrary } from "@shoelace-style/shoelace/dist/utilities/icon-library.js";

import { SlDropdown } from "@shoelace-style/shoelace";
import "@shoelace-style/shoelace/dist/components/dropdown/dropdown.js";
import "@shoelace-style/shoelace/dist/components/menu/menu.js";
import "@shoelace-style/shoelace/dist/components/menu-item/menu-item.js";
import "@shoelace-style/shoelace/dist/components/button-group/button-group.js";
import "@shoelace-style/shoelace/dist/components/button/button.js";
import "@shoelace-style/shoelace/dist/components/icon/icon.js";

registerIconLibrary("component-assets", {
    resolver: (name) => `/assets/text-editor/menu-bar/${name}.svg`,
});

registerIconLibrary("primeicons", {
    resolver: (name) => {
        const shortName = name.replace("pi-", "");
        return `/assets/primeicons/${shortName}.svg`;
    },
    mutator: (svg) => {
        // Scale the icon up, so it's the same size as others.
        svg.style.width = "auto";
        svg.style.height = "1.4rem";
        svg.style.margin = "-0.2rem";
        svg.setAttribute("width", "16");
        svg.setAttribute("height", "16");
        svg.setAttribute("fill", "currentColor");
    },
});

/**
 * Returns the menu items sorted based on the order property.
 */
const sortItems = <T extends { order?: number }>(items: T[] = []): T[] => {
    return items.toSorted((a, b) => {
        return (
            (a.order ?? Number.MAX_SAFE_INTEGER) -
            (b.order ?? Number.MAX_SAFE_INTEGER)
        );
    });
};

const iconTemplate = ({
    name,
    library = "component-assets",
    slot = "prefix",
}: {
    name: string;
    library?: string;
    slot?: string;
}) => html` <sl-icon name=${name} library=${library} slot=${slot}></sl-icon>`;

const itemTemplates = {
    toggle: (item: MenuItemState) =>
        html`<sl-tooltip placement="top" content=${item.label}>
            <sl-button
                class=${classMap({
                    "stekz-button": true,
                    "pressed": item.active,
                })}
                ?caret=${item?.caret}
                ?disabled=${item?.disabled}
                @click=${(e: MouseEvent) => {
                    item.run?.(e);
                }}
            >
                ${item?.icon ? iconTemplate(item.icon) : nothing}
                ${item?.showLabel ? item?.label : nothing}
            </sl-button>
        </sl-tooltip>`,
    dropdown: (item: MenuItemState) =>
        html`<c-menu-bar-dropdown .item=${item}></c-menu-bar-dropdown>`,
};

@customElement("c-menu-bar-dropdown")
class _StekzMenuBarDropdown extends LitElement {
    @property()
    item: MenuItemState;

    dropdown: Ref<SlDropdown> = createRef();

    @state()
    _subItems: MenuItemState["items"];

    async willUpdate(changedProperties: PropertyValues<this>) {
        if (changedProperties.has("item")) {
            if (this.item?.getItems) {
                this._subItems = sortItems(await this.item.getItems());
            } else if (this.item?.items?.length) {
                this._subItems = sortItems(this.item.items);
            } else {
                this._subItems = [];
            }
        }
    }

    render() {
        return html`<sl-dropdown ${ref(this.dropdown)}>
            <sl-button
                class="stekz-button"
                slot="trigger"
                ?caret=${this.item?.caret}
                ?disabled=${this.item?.disabled}
            >
                ${this.item?.icon ? iconTemplate(this.item.icon) : nothing}
                ${this.item?.showLabel ? this.item?.label : nothing}
            </sl-button>
            <sl-menu>
                ${this._subItems
                    ? sortItems(this._subItems).map(
                          (menuItem) => html`
                              <sl-menu-item
                                  type=${menuItem?.active
                                      ? "checkbox"
                                      : "normal"}
                                  @click=${(e: MouseEvent) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      this.dropdown.value?.hide?.();
                                      menuItem?.run(e);
                                  }}
                                  ?checked=${menuItem?.active}
                                  ?disabled=${menuItem?.disabled &&
                                  !menuItem?.active}
                              >
                                  ${menuItem?.icon
                                      ? iconTemplate(menuItem.icon)
                                      : nothing}
                                  ${menuItem.label}
                              </sl-menu-item>
                          `
                      )
                    : html`<div>Loading...</div>`}
            </sl-menu>
        </sl-dropdown>`;
    }
}

@customElement("c-menu-bar")
export class StekzMenuBar extends LitElement {
    @property({ type: Array, attribute: false })
    items?: Array<MenuItemState> = [];

    _itemsPerGroup: Record<number, Array<MenuItemState>> = [];

    async willUpdate(changedProperties: PropertyValues<this>) {
        if (changedProperties.has("items")) {
            this._itemsPerGroup = Object.groupBy(
                sortItems(this.items),
                (item) => item.group
            );
        }
    }

    static styles = [
        unsafeCSS(shoelaceStekzTheme),
        css`
            /* Shoelace button does not define a "pressed" state by default, so we define
                a class to style a button as pressed. */
            .stekz-button.pressed:is([disabled], :not(:active))::part(base) {
                background: var(--stekz-secondary-background);
                border-color: var(--sl-color-neutral-400);
            }

            .stekz-button::part(base):focus-visible {
                box-shadow: 0 0 0 2px var(--sl-color-primary-400);
            }

            .stekz-button[disabled]::part(base) {
                cursor: default;
            }
        `,
    ];

    render() {
        return repeat(
            Object.entries(this._itemsPerGroup),
            ([group, _]) => group,
            ([group, items]) => {
                return html`<sl-button-group group=${group}>
                    ${repeat(
                        items,
                        (item) => item.name,
                        (item) => itemTemplates?.[item.type](item)
                    )}
                </sl-button-group>`;
            }
        );
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "c-menu-bar": StekzMenuBar;
    }
}
